import { uiText, useUiLanguage } from '../../utils/uiTranslation';
/**
 * FormFieldsModal.js
 * Simple modal for customizing which fields appear on the public manpower request form
 */
import React, { useState, useEffect } from 'react';
import { ref, get, update } from 'firebase/database';
import { database } from '../../firebase';
import './ManpowerRequest.css';

// Default field configuration
const DEFAULT_FIELDS = [
    // Requester Info
    { id: 'requesterName', label: 'Requester Name', section: 'requester', type: 'text', required: true, enabled: true, locked: true },
    { id: 'requesterEmail', label: 'Requester Email', section: 'requester', type: 'email', required: true, enabled: true, locked: true },
    { id: 'requesterDepartment', label: 'Department', section: 'requester', type: 'select', required: true, enabled: true, locked: false },
    { id: 'requesterLocation', label: 'Location / Property', section: 'requester', type: 'select', required: false, enabled: true, locked: false },

    // Request Type
    { id: 'requestType', label: 'Request Type (New/Replacement)', section: 'type', type: 'radio', required: true, enabled: true, locked: true },
    { id: 'budgetStatus', label: 'Budget Status', section: 'type', type: 'select', required: true, enabled: true, locked: false },

    // Position Details
    { id: 'jobTitle', label: 'Job Title', section: 'position', type: 'text', required: true, enabled: true, locked: true },
    { id: 'headcount', label: 'Number of Positions', section: 'position', type: 'number', required: true, enabled: true, locked: false },
    { id: 'employmentType', label: 'Employment Type', section: 'position', type: 'radio', required: true, enabled: true, locked: false },
    { id: 'gradeCode', label: 'Grade / Band Code', section: 'position', type: 'select', required: false, enabled: false, locked: false },
    { id: 'salaryRange', label: 'Salary Range', section: 'position', type: 'text', required: false, enabled: true, locked: false },

    // Compliance
    { id: 'nationalityPref', label: 'Nationality Preference', section: 'compliance', type: 'select', required: false, enabled: true, locked: false },
    { id: 'visaRequirement', label: 'Visa / Work Permit Status', section: 'compliance', type: 'select', required: false, enabled: true, locked: false },

    // Justification
    { id: 'urgency', label: 'Urgency Level', section: 'justification', type: 'radio', required: true, enabled: true, locked: true },
    { id: 'justification', label: 'Business Justification', section: 'justification', type: 'textarea', required: true, enabled: true, locked: true },
    { id: 'costCenter', label: 'Cost Center / Budget Code', section: 'justification', type: 'text', required: false, enabled: true, locked: false },

    // Optional
    { id: 'skills', label: 'Required Skills', section: 'optional', type: 'text', required: false, enabled: true, locked: false },
    { id: 'experienceLevel', label: 'Experience Level', section: 'optional', type: 'select', required: false, enabled: true, locked: false },
    { id: 'reportingTo', label: 'Reports To', section: 'optional', type: 'text', required: false, enabled: true, locked: false }
];

const SECTION_LABELS = {
    requester: 'Requester Information',
    type: 'Request Type',
    position: 'Position Details',
    compliance: 'Visa & Nationality',
    justification: 'Urgency & Justification',
    optional: 'Additional Details'
};

const TYPE_LABELS = {
    text: 'Text',
    email: 'Email',
    number: 'Number',
    select: 'Dropdown',
    radio: 'Choice',
    textarea: 'Long Text'
};

const FormFieldsModal = ({ companyUID, onClose }) => {
  useUiLanguage();
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Load field configuration
    useEffect(() => {
        const loadFields = async () => {
            if (!companyUID) return;
            try {
                const fieldsRef = ref(database, `companies/${companyUID}/settings/manpowerRequest/formFields`);
                const snapshot = await get(fieldsRef);

                if (snapshot.exists()) {
                    // Merge saved config with defaults (in case new fields were added)
                    const savedFields = snapshot.val();
                    const mergedFields = DEFAULT_FIELDS.map(defaultField => {
                        const savedField = savedFields.find(f => f.id === defaultField.id);
                        return savedField ? { ...defaultField, ...savedField } : defaultField;
                    });
                    setFields(mergedFields);
                } else {
                    setFields(DEFAULT_FIELDS);
                }
            } catch (err) {
                console.error('Error loading form fields:', err);
                setFields(DEFAULT_FIELDS);
            } finally {
                setLoading(false);
            }
        };
        loadFields();
    }, [companyUID]);

    // Toggle field enabled state
    const toggleField = (fieldId) => {
        setFields(prev => prev.map(f =>
            f.id === fieldId && !f.locked ? { ...f, enabled: !f.enabled } : f
        ));
    };

    // Toggle required state
    const toggleRequired = (fieldId) => {
        setFields(prev => prev.map(f =>
            f.id === fieldId && !f.locked && f.enabled ? { ...f, required: !f.required } : f
        ));
    };

    // Save configuration
    const handleSave = async () => {
        setSaving(true);
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                formFields: fields
            });
            onClose();
        } catch (err) {
            console.error('Error saving form fields:', err);
            alert(uiText('Failed to save. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    // Group fields by section
    const groupedFields = fields.reduce((acc, field) => {
        if (!acc[field.section]) acc[field.section] = [];
        acc[field.section].push(field);
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="manpower-modal-overlay" onClick={onClose}>
                <div className="manpower-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px', color: '#568ded' }} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="manpower-modal-overlay" onClick={onClose}>
            <div className="manpower-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh' }}>
                {/* Header */}
                <div className="manpower-modal-header">
                    <h2>{uiText("Customize Form Fields")}</h2>
                    <button className="manpower-modal-close" onClick={onClose}>×</button>
                </div>

                {/* Body */}
                <div className="manpower-modal-body" style={{ maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}>
                    <p style={{
                        color: "rgba(var(--app-ink-rgb, 0, 0, 0),0.6)",
                        fontSize: '13px',
                        marginBottom: '20px',
                        padding: '12px',
                        backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
                        borderRadius: '8px'
                    }}>{uiText("Toggle which fields appear on the public request form. Locked fields (")}<i className="fa-solid fa-lock" style={{ fontSize: '10px' }} />{uiText(") are required for the system to function.")}</p>

                    {Object.entries(SECTION_LABELS).map(([sectionKey, sectionLabel]) => (
                        groupedFields[sectionKey] && (
                            <div key={sectionKey} style={{ marginBottom: '24px' }}>
                                <h4 style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#568ded',
                                    textTransform: 'none',
                                    letterSpacing: '0.5px',
                                    marginBottom: '12px',
                                    paddingBottom: '8px',
                                    borderBottom: '1px solid #E0E0E0'
                                }}>
                                    {uiText(sectionLabel)}
                                </h4>

                                {groupedFields[sectionKey].map(field => (
                                    <div
                                        key={field.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '10px 12px',
                                            backgroundColor: field.enabled ? 'rgba(46, 204, 113, 0.05)' : 'rgba(0,0,0,0.02)',
                                            borderRadius: '8px',
                                            marginBottom: '8px',
                                            opacity: field.enabled ? 1 : 0.6
                                        }}
                                    >
                                        {/* Enable Toggle */}
                                        <button
                                            onClick={() => toggleField(field.id)}
                                            disabled={field.locked}
                                            style={{ '--light-control-fill': field.enabled ? '#2ecc71' : '#ccc',
                                                width: '36px',
                                                height: '20px',
                                                borderRadius: '10px',
                                                border: 'none',
                                                cursor: field.locked ? 'not-allowed' : 'pointer',
                                                backgroundColor: field.enabled ? '#2ecc71' : '#ccc',
                                                position: 'relative',
                                                marginRight: '12px',
                                                flexShrink: 0
                                            }}
                                          data-light-control={field.enabled ? "filled" : "quiet"}>
                                            <span style={{
                                                position: 'absolute',
                                                top: '2px',
                                                left: field.enabled ? '18px' : '2px',
                                                width: '16px',
                                                height: '16px',
                                                borderRadius: '50%',
                                                backgroundColor: '#fff',
                                                transition: 'left 0.2s ease'
                                            }} />
                                        </button>

                                        {/* Field Info */}
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontWeight: 500, fontSize: '14px' }}>
                                                    {uiText(field.label)}
                                                </span>
                                                {field.locked && (
                                                    <i className="fa-solid fa-lock" style={{ fontSize: '10px', color: '#999' }} />
                                                )}
                                            </div>
                                            <span style={{ fontSize: '11px', color: '#999' }}>
                                                {TYPE_LABELS[field.type]}
                                            </span>
                                        </div>

                                        {/* Required Toggle */}
                                        {field.enabled && !field.locked && (
                                            <button
                                                onClick={() => toggleRequired(field.id)}
                                                style={{ '--light-control-fill': field.required ? '#e74c3c' : '#eee',
                                                    padding: '4px 10px',
                                                    fontSize: '11px',
                                                    borderRadius: '12px',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    backgroundColor: field.required ? '#e74c3c' : '#eee',
                                                    color: field.required ? 'var(--app-text, #fff)' : '#666',
                                                    fontWeight: 500
                                                }}
                                              data-light-control={field.required ? "filled" : "quiet"}>
                                                {field.required ? uiText("Required") : uiText("Optional")}
                                            </button>
                                        )}
                                        {field.locked && field.required && (
                                            <span style={{
                                                padding: '4px 10px',
                                                fontSize: '11px',
                                                borderRadius: '12px',
                                                backgroundColor: '#e74c3c',
                                                color: '#fff',
                                                fontWeight: 500
                                            }}>{uiText("Required")}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )
                    ))}
                </div>

                {/* Footer */}
                <div className="manpower-modal-footer" style={{ justifyContent: 'flex-end' }}>
                    <button
                        className="manpower-btn manpower-btn-secondary"
                        onClick={onClose}
                        style={{ marginRight: '10px' }}
                    >{uiText("Cancel")}</button>
                    <button
                        className="manpower-btn manpower-btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? uiText("Saving...") : uiText("Save Changes")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FormFieldsModal;
